import {
  Autocomplete,
  Box,
  Card,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { getUsersUnderAdmin } from "../../../../api/project/Project";
import useAuth from "../../../../hooks/useAuth";
import ColorSelector from "./ColorSelector";
import NotificationList from "./NotificationList";

const UNIT_LABELS = {
  day: { singular: "day", plural: "days" },
  week: { singular: "week", plural: "weeks" },
  month: { singular: "month", plural: "months" },
  year: { singular: "year", plural: "years" },
};

const EventFormFields = ({
  startDate,
  endDate,
  eventStartTime,
  eventEndTime,
  isEventAllDay,
  eventRepeat,
  selectedColor,
  colorAnchorEl,
  notifications,
  location,
  description,
  selectedUsers,
  // Custom recurrence props
  customRepeatEvery,
  customRepeatUnit,
  customMonthlyPattern,
  customWeeklyDays,
  customEndMode,
  customEndOnDate,
  customEndAfterCount,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onAllDayChange,
  onRepeatChange,
  onColorMenuOpen,
  onColorMenuClose,
  onColorSelect,
  onNotificationChange,
  onRemoveNotification,
  onAddNotification,
  onCustomRecurrenceOpen,
  onLocationChange,
  onDescriptionChange,
  onUsersChange,
  isEditing = false,
}) => {
  const [userList, setUserList] = useState([]);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUsersUnderAdmin();
        if (res?.data?.success) {
          setUserList(res.data.data.peopleDropdown || []);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Date and Time Fields - Hidden when editing */}
      {!isEditing && (
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: isEventAllDay ? 0 : 2,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  flex: isEventAllDay ? "1 1 100%" : "1 1 50%",
                  transition: "flex 0.25s ease",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Start date"
                    value={startDate}
                    onChange={onStartDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box
                sx={{
                  flex: isEventAllDay ? "0 0 0%" : "1 1 50%",
                  maxWidth: isEventAllDay ? 0 : "50%",
                  opacity: isEventAllDay ? 0 : 1,
                  transform: isEventAllDay ? "translateY(-4px)" : "translateY(0)",
                  overflow: "hidden",
                  pointerEvents: isEventAllDay ? "none" : "auto",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  transition:
                    "flex 0.25s ease, max-width 0.25s ease, opacity 0.2s ease, transform 0.2s ease",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <TimePicker
                    label="Start time"
                    value={eventStartTime}
                    onChange={onStartTimeChange}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        sx: { minWidth: 120 },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Typography color="textSecondary">-</Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <TimePicker
                    label="End time"
                    value={eventEndTime}
                    onChange={onEndTimeChange}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        sx: { minWidth: 120 },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                maxHeight: isEventAllDay ? 80 : 0,
                opacity: isEventAllDay ? 1 : 0,
                transform: isEventAllDay ? "translateY(0)" : "translateY(-4px)",
                overflow: "hidden",
                pointerEvents: isEventAllDay ? "auto" : "none",
                transition:
                  "max-height 0.25s ease, opacity 0.2s ease, transform 0.2s ease",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="End date"
                  value={endDate}
                  minDate={startDate || undefined}
                  onChange={onEndDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={isEventAllDay} onChange={onAllDayChange} />
              }
              label="All day"
            />
          </Grid>
        </>
      )}

      {/* Repeat Section - Hidden when editing */}
      {!isEditing && (
        <Grid item xs={12}>
        <ToggleButtonGroup
          value={eventRepeat}
          exclusive
          onChange={(e, value) => {
            if (value !== null) {
              onRepeatChange(value);
              if (value === "custom") {
                onCustomRecurrenceOpen();
              }
            }
          }}
          fullWidth
          sx={{
            "& .MuiToggleButton-root": {
              py: 1.25,
              textTransform: "none",
              fontWeight: 500,
              borderColor: "divider",
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            },
          }}
        >
          <ToggleButton value="none">Does not repeat</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
        </ToggleButtonGroup>

        {/* Custom Recurrence Summary Card */}
        {eventRepeat === "custom" && (
          <Card
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "grey.50",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: "grey.100",
                borderColor: "primary.main",
              },
            }}
            onClick={onCustomRecurrenceOpen}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Recurrence Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Repeats every {customRepeatEvery}{" "}
                  {customRepeatEvery === 1
                    ? UNIT_LABELS[customRepeatUnit]?.singular
                    : UNIT_LABELS[customRepeatUnit]?.plural}
                </Typography>
                {customRepeatUnit === "week" && customWeeklyDays?.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    On: {customWeeklyDays.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ")}
                  </Typography>
                )}
                {customRepeatUnit === "month" && (
                  <Typography variant="body2" color="text.secondary">
                    {customMonthlyPattern === "day-of-month"
                      ? `On day ${startDate ? moment(startDate).date() : "—"}`
                      : `On the ${startDate ? moment(startDate).format("Do dddd") : "—"}`}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {customEndMode === "on"
                    ? `Ends on ${customEndOnDate ? moment(customEndOnDate).format("MMM D, YYYY") : "—"}`
                    : `Ends after ${customEndAfterCount} occurrences`}
                </Typography>
              </Box>
              <IconButton size="small" color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Card>
        )}
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Add location"
          placeholder="Add location"
          variant="outlined"
          value={location || ""}
          onChange={(e) => onLocationChange?.(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Add description or attachment"
          placeholder="Add description or attachment"
          multiline
          rows={3}
          variant="outlined"
          value={description || ""}
          onChange={(e) => onDescriptionChange?.(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={userList}
          value={selectedUsers || []}
          onChange={(event, newValue) => {
            onUsersChange?.(newValue);
          }}
          getOptionLabel={(option) =>
            `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
            "Unknown"
          }
          getOptionSelected={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add attendees"
              placeholder="Select users"
              variant="outlined"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={`${option.first_name || ""} ${option.last_name || ""}`.trim()}
                size="small"
              />
            ))
          }
        />
      </Grid>
      <Grid item xs={12}>
        <ColorSelector
          selectedColor={selectedColor}
          colorAnchorEl={colorAnchorEl}
          onColorMenuOpen={onColorMenuOpen}
          onColorMenuClose={onColorMenuClose}
          onColorSelect={onColorSelect}
          userName={
            currentUser
              ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
              : undefined
          }
        />
      </Grid>
      <NotificationList
        notifications={notifications}
        onNotificationChange={onNotificationChange}
        onRemoveNotification={onRemoveNotification}
        onAddNotification={onAddNotification}
      />
    </Grid>
  );
};

EventFormFields.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  eventStartTime: PropTypes.object,
  eventEndTime: PropTypes.object,
  isEventAllDay: PropTypes.bool.isRequired,
  eventRepeat: PropTypes.string.isRequired,
  selectedColor: PropTypes.string.isRequired,
  colorAnchorEl: PropTypes.object,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      amount: PropTypes.number.isRequired,
      unit: PropTypes.string.isRequired,
      time: PropTypes.object,
    })
  ).isRequired,
  location: PropTypes.string,
  description: PropTypes.string,
  selectedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    })
  ),
  // Custom recurrence props
  customRepeatEvery: PropTypes.number,
  customRepeatUnit: PropTypes.string,
  customMonthlyPattern: PropTypes.string,
  customWeeklyDays: PropTypes.arrayOf(PropTypes.string),
  customEndMode: PropTypes.string,
  customEndOnDate: PropTypes.object,
  customEndAfterCount: PropTypes.number,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  onStartTimeChange: PropTypes.func.isRequired,
  onEndTimeChange: PropTypes.func.isRequired,
  onAllDayChange: PropTypes.func.isRequired,
  onRepeatChange: PropTypes.func.isRequired,
  onColorMenuOpen: PropTypes.func.isRequired,
  onColorMenuClose: PropTypes.func.isRequired,
  onColorSelect: PropTypes.func.isRequired,
  onNotificationChange: PropTypes.func.isRequired,
  onRemoveNotification: PropTypes.func.isRequired,
  onAddNotification: PropTypes.func.isRequired,
  onCustomRecurrenceOpen: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func,
  onDescriptionChange: PropTypes.func,
  onUsersChange: PropTypes.func,
  isEditing: PropTypes.bool,
};

export default EventFormFields;

